import { baseToApiShape, baseFromApiShape, baseInitNewForm, baseInitEditForm } from '../formHelpers/base';

/**
 * Azure-specific flat → nested API payload.
 * Extends base with Azure fields: disk types, proximity placement, capacity auto-expire,
 * static IP, image resource group, marketplace image references.
 */
export function toApiShape(form) {
    const base = baseToApiShape(form);
    return {
        ...base,
        info: {
            ...base.info,
            prefix: form.prefix || '',
            marketplace_head_image_id: form.marketplaceHeadImageId || null,
            marketplace_compute_image_id: form.marketplaceComputeImageId || null,
        },
        data: {
            ...base.data,
            image_resource_group_name: form.imageResourceGroupName || '',
            marketplace_image_reference: form.marketplaceImageReference || null,
            server: {
                ...base.data.server,
                disk_type: form.serverDiskType || '',
                ocpus: form.serverOcpus || 1,
                mem: form.serverMem || 1,
            },
            network: {
                ...base.data.network,
                use_proximity_placement: form.useProximityPlacement || false,
                private_static_ip: form.privateStaticIp || null,
                capacity_reservation_auto_expire: form.capacityReservationAutoExpire ?? true,
                capacity_reservation_expire_hours: form.capacityReservationExpireHours ?? 1,
            },
            node: {
                ...base.data.node,
                disk_type: form.nodeDiskType || '',
            },
        },
    };
}

/**
 * Azure-specific nested → flat form state.
 */
export function fromApiShape(cluster) {
    return {
        ...baseFromApiShape(cluster),

        // Azure info fields
        prefix: cluster.info?.prefix || '',
        marketplaceHeadImageId: cluster.info?.marketplace_head_image_id || null,
        marketplaceComputeImageId: cluster.info?.marketplace_compute_image_id || null,

        // Azure server fields
        serverDiskType: cluster.data?.server?.disk_type || '',
        serverOcpus: cluster.data?.server?.ocpus || 1,
        serverMem: cluster.data?.server?.mem || 1,

        // Azure network fields
        useProximityPlacement: cluster.data?.network?.use_proximity_placement || false,
        privateStaticIp: cluster.data?.network?.private_static_ip || null,
        capacityReservationAutoExpire: cluster.data?.network?.capacity_reservation_auto_expire ?? true,
        capacityReservationExpireHours: cluster.data?.network?.capacity_reservation_expire_hours ?? 1,

        // Azure data fields
        imageResourceGroupName: cluster.data?.image_resource_group_name || '',
        marketplaceImageReference: cluster.data?.marketplace_image_reference || null,
        nodeDiskType: cluster.data?.node?.disk_type || '',
    };
}

/**
 * Azure new cluster defaults.
 */
export function initNewForm(provider, config) {
    return {
        ...baseInitNewForm(provider, config),
        capacityReservationAutoExpire: true,
        capacityReservationExpireHours: 1,
        useProximityPlacement: false,
        serverDiskType: '',
        nodeDiskType: '',
        // Marketplace image references (if marketplace stack)
        marketplaceHeadImageId: config.marketplaceConfig?.marketplace_head_image_id || null,
        marketplaceComputeImageId: config.marketplaceConfig?.marketplace_compute_image_id
            || config.marketplaceConfig?.marketplace_head_image_id || null,
        marketplaceImageReference: config.marketplaceConfig ? {
            publisher: config.marketplaceConfig.publisher || 'paratools-inc',
            offer: config.marketplaceConfig.offer || 'pt-pro-4-e4s-msa-cyclecloud-amd64',
            sku: config.marketplaceConfig.sku || 'paratools-pro-cyclecloud-amd64',
            marketplace_image_version: config.marketplaceConfig.marketplace_image_version || 'latest',
            requires_plan: config.marketplaceConfig.requires_plan || false,
        } : null,
    };
}

/**
 * Azure edit-mode initialization.
 */
export function initEditForm(cluster, config) {
    const flat = {
        ...baseInitEditForm(cluster, config),

        // Azure-specific from cluster
        prefix: cluster.info?.prefix || '',
        marketplaceHeadImageId: cluster.info?.marketplace_head_image_id || null,
        marketplaceComputeImageId: cluster.info?.marketplace_compute_image_id || null,
        serverDiskType: cluster.data?.server?.disk_type || '',
        serverOcpus: cluster.data?.server?.ocpus || 1,
        serverMem: cluster.data?.server?.mem || 1,
        useProximityPlacement: cluster.data?.network?.use_proximity_placement || false,
        privateStaticIp: cluster.data?.network?.private_static_ip || null,
        capacityReservationAutoExpire: cluster.data?.network?.capacity_reservation_auto_expire ?? true,
        capacityReservationExpireHours: cluster.data?.network?.capacity_reservation_expire_hours ?? 1,
        imageResourceGroupName: cluster.data?.image_resource_group_name || '',
        marketplaceImageReference: cluster.data?.marketplace_image_reference || null,
        nodeDiskType: cluster.data?.node?.disk_type || '',
    };

    return flat;
}

/**
 * Check if any instance in the cluster supports proximity placement groups.
 * HB-series, HC-series, ND-series, NC-series, H16r/H16mr.
 */
export function shouldEnableProximityPlacement(form) {
    const check = (id) => {
        if (!id) return false;
        if (id.startsWith('Standard_HB')) return true;
        if (id.startsWith('Standard_HC')) return true;
        if (id.startsWith('Standard_ND')) return true;
        if (id.startsWith('Standard_NC')) return true;
        if (id === 'Standard_H16r' || id === 'Standard_H16mr') return true;
        return false;
    };
    if (check(form?.serverInstanceId)) return true;
    return (form?.nodeGroups || []).some(g => check(g.instance_id));
}
